import { type FormEvent, useId, useRef, useState } from "react";
import { ArrowLeft, RotateCcw } from "lucide-react";
import { PrivacyCheckbox } from "@/components/legal/PrivacyCheckbox";
import { RayMeter } from "@/components/brand/RayMeter";
import { Button } from "@/components/ui/Button";
import { AuditButton, CtaReassurance, WhatsAppButton } from "@/components/ui/Cta";
import { PageHero } from "@/components/ui/PageHero";
import { CONTACT_FORM_ENDPOINT, QUIZ_PATH } from "@/config/constants";
import {
  QUIZ_ANSWERS,
  QUIZ_AREA_TIPS,
  QUIZ_BANDS,
  QUIZ_MAX,
  QUIZ_QUESTIONS,
  QUIZ_UI,
  type QuizArea,
} from "@/data/quiz";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { useLocale } from "@/i18n/useLocale";
import { trackEvent } from "@/utils/analytics";

const inputCls =
  "w-full rounded-xl border border-brand-primary/15 bg-white px-4 py-3 text-base text-ink outline-none transition-colors focus:border-teal-strong focus:ring-2 focus:ring-brand-secondary/40 aria-[invalid=true]:border-red-700";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Stage = "intro" | "questions" | "gate" | "result";

function scoreOf(answers: readonly number[]) {
  return answers.reduce((sum, n) => sum + n, 0);
}

/** The area with the lowest share of its possible points; ties go to the earlier area. */
function weakestArea(answers: readonly number[]): QuizArea {
  const totals = new Map<QuizArea, { got: number; max: number }>();
  QUIZ_QUESTIONS.forEach((q, i) => {
    const t = totals.get(q.area) ?? { got: 0, max: 0 };
    totals.set(q.area, { got: t.got + (answers[i] ?? 0), max: t.max + 2 });
  });
  let worst: QuizArea = QUIZ_QUESTIONS[0].area;
  let worstRatio = Infinity;
  for (const [area, t] of totals) {
    const ratio = t.got / t.max;
    if (ratio < worstRatio) {
      worst = area;
      worstRatio = ratio;
    }
  }
  return worst;
}

/** Lead magnet: a 9-question scorecard, results behind a short email step. */
export function QuizPage() {
  usePageSeo(QUIZ_PATH);
  const locale = useLocale();
  const tx = (copy: { es: string; en: string }) => copy[locale];
  const { t } = useAppTranslation("common");
  const formId = useId();
  const topRef = useRef<HTMLDivElement>(null);

  const [stage, setStage] = useState<Stage>("intro");
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [privacy, setPrivacy] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [privacyError, setPrivacyError] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [sending, setSending] = useState(false);

  const total = QUIZ_QUESTIONS.length;
  const score = scoreOf(answers);
  const band = QUIZ_BANDS.find((b) => score >= b.min) ?? QUIZ_BANDS[QUIZ_BANDS.length - 1];
  const weak = QUIZ_AREA_TIPS[weakestArea(answers)];

  function show(next: Stage) {
    setStage(next);
    requestAnimationFrame(() => {
      topRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
      topRef.current?.querySelector<HTMLElement>("[data-focus]")?.focus({ preventScroll: true });
    });
  }

  function answer(points: number) {
    const next = [...answers.slice(0, index), points];
    setAnswers(next);
    if (index + 1 < total) {
      setIndex(index + 1);
      requestAnimationFrame(() => topRef.current?.querySelector<HTMLElement>("[data-focus]")?.focus({ preventScroll: true }));
    } else {
      show("gate");
    }
  }

  function back() {
    if (stage === "gate") {
      setStage("questions");
      return;
    }
    if (index > 0) setIndex(index - 1);
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (sending) return;
    const data = new FormData(e.currentTarget);
    const name = String(data.get("name") ?? "").trim();
    const email = String(data.get("email") ?? "").trim();
    const fieldsBad = name.length < 2 || !EMAIL_RE.test(email);
    setInvalid(fieldsBad);
    setPrivacyError(!privacy);
    if (fieldsBad || !privacy) return;
    setSending(true);
    const lines = QUIZ_QUESTIONS.map((q, i) => {
      const label = QUIZ_ANSWERS.find((a) => a.score === answers[i])?.label.es ?? "—";
      return `- ${q.text.es} ${label}`;
    });
    const message = [
      `Resultado del test de Instagram: ${score}/${QUIZ_MAX} — ${band.title.es}`,
      `Punto más flojo: ${weak.name.es}`,
      "",
      ...lines,
    ].join("\n");
    try {
      await fetch(CONTACT_FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, message, source: "quiz", score, marketingConsent: marketing }),
      });
    } catch {
      /* The result is shown either way: a failed email must not hide it. */
    }
    trackEvent("quiz_complete", { location: "quiz", score: String(score) });
    setSending(false);
    show("result");
  }

  function restart() {
    setAnswers([]);
    setIndex(0);
    setPrivacy(false);
    setMarketing(false);
    show("questions");
  }

  const whatsappMessage = tx(QUIZ_UI.whatsapp).replace("{score}", String(score)).replace("{max}", String(QUIZ_MAX));

  return (
    <main id="main-content" tabIndex={-1} className="outline-none">
      <PageHero crumb={tx(QUIZ_UI.crumb)} title={tx(QUIZ_UI.title)} lead={tx(QUIZ_UI.lead)} />

      <section aria-labelledby={`${formId}-heading`} className="bg-white">
        <div ref={topRef} className="mx-auto max-w-2xl scroll-mt-32 px-4 py-16 sm:px-6 md:py-24">
          {stage === "intro" ? (
            <div className="text-center">
              <h2 id={`${formId}-heading`} className="sr-only">
                {tx(QUIZ_UI.title)}
              </h2>
              <Button variant="primary" size="lg" arrow onClick={() => show("questions")}>
                {tx(QUIZ_UI.start)}
              </Button>
            </div>
          ) : null}

          {stage === "questions" ? (
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.14em] text-teal-strong">
                {tx(QUIZ_UI.question).replace("{current}", String(index + 1)).replace("{total}", String(total))}
              </p>
              <div
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={total}
                aria-valuenow={index}
                aria-label={tx(QUIZ_UI.crumb)}
                className="mt-3 h-1.5 overflow-hidden rounded-full bg-brand-primary/10"
              >
                <div
                  className="h-full rounded-full bg-teal-strong transition-[width] duration-500"
                  style={{ width: `${(index / total) * 100}%` }}
                />
              </div>
              <h2
                id={`${formId}-heading`}
                data-focus=""
                tabIndex={-1}
                className="mt-8 font-display text-[1.875rem] font-semibold leading-tight text-brand-primary outline-none"
              >
                {tx(QUIZ_QUESTIONS[index].text)}
              </h2>
              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                {QUIZ_ANSWERS.map((option) => (
                  <button
                    key={option.score}
                    type="button"
                    onClick={() => answer(option.score)}
                    aria-pressed={answers[index] === option.score}
                    className="min-h-14 rounded-2xl border border-brand-primary/15 bg-brand-bg px-5 text-base font-semibold text-brand-primary transition-colors hover:border-teal-strong hover:bg-teal-soft aria-pressed:border-teal-strong aria-pressed:bg-teal-soft"
                  >
                    {tx(option.label)}
                  </button>
                ))}
              </div>
              {index > 0 ? (
                <button
                  type="button"
                  onClick={back}
                  className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-ink-muted hover:text-brand-primary"
                >
                  <ArrowLeft aria-hidden="true" className="size-4" />
                  {tx(QUIZ_UI.back)}
                </button>
              ) : null}
            </div>
          ) : null}

          {stage === "gate" ? (
            <form noValidate onSubmit={submit}>
              <h2
                id={`${formId}-heading`}
                data-focus=""
                tabIndex={-1}
                className="font-display text-[1.875rem] font-semibold leading-tight text-brand-primary outline-none"
              >
                {tx(QUIZ_UI.gateTitle)}
              </h2>
              <p className="mt-3 leading-relaxed text-ink-soft">{tx(QUIZ_UI.gateBody)}</p>
              <div className="mt-8 space-y-5">
                <div>
                  <label htmlFor={`${formId}-name`} className="block font-medium text-brand-primary">
                    {tx(QUIZ_UI.name)}
                  </label>
                  <input
                    id={`${formId}-name`}
                    name="name"
                    autoComplete="name"
                    required
                    maxLength={200}
                    aria-invalid={invalid || undefined}
                    className={`mt-2 ${inputCls}`}
                  />
                </div>
                <div>
                  <label htmlFor={`${formId}-email`} className="block font-medium text-brand-primary">
                    {tx(QUIZ_UI.email)}
                  </label>
                  <input
                    id={`${formId}-email`}
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    maxLength={254}
                    aria-invalid={invalid || undefined}
                    className={`mt-2 ${inputCls}`}
                  />
                </div>
                <PrivacyCheckbox
                  checked={privacy}
                  onChange={(v) => {
                    setPrivacy(v);
                    if (v) setPrivacyError(false);
                  }}
                  disabled={sending}
                  suffix={tx(QUIZ_UI.privacySuffix)}
                />
                <label className="flex items-start gap-3 text-sm leading-relaxed text-ink-soft">
                  <input
                    type="checkbox"
                    checked={marketing}
                    onChange={(e) => setMarketing(e.target.checked)}
                    disabled={sending}
                    className="mt-0.5 size-5 shrink-0 rounded border-brand-primary/30 accent-[#2f6b67]"
                  />
                  <span>{tx(QUIZ_UI.marketing)}</span>
                </label>
                {invalid ? (
                  <p role="alert" className="text-sm font-medium text-red-700">
                    {tx(QUIZ_UI.invalid)}
                  </p>
                ) : null}
                {privacyError ? (
                  <p role="alert" className="text-sm font-medium text-red-700">
                    {tx(QUIZ_UI.privacyError)}
                  </p>
                ) : null}
              </div>
              <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={back}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-ink-muted hover:text-brand-primary"
                >
                  <ArrowLeft aria-hidden="true" className="size-4" />
                  {tx(QUIZ_UI.back)}
                </button>
                <Button type="submit" variant="primary" size="lg" arrow disabled={sending} aria-busy={sending}>
                  {sending ? tx(QUIZ_UI.sending) : tx(QUIZ_UI.submit)}
                </Button>
              </div>
            </form>
          ) : null}

          {stage === "result" ? (
            <div role="status">
              <div className="flex flex-col items-center text-center">
                <RayMeter lit={score} total={QUIZ_MAX} className="size-28" />
                <p className="mt-6 text-sm font-semibold uppercase tracking-[0.14em] text-teal-strong">
                  {tx(QUIZ_UI.scoreLabel)}
                </p>
                <p className="mt-1 font-display text-[clamp(2.6rem,6.2vw,4.75rem)] font-semibold leading-none text-brand-primary [font-variant-numeric:lining-nums_tabular-nums]">
                  {score}
                  <span className="text-[0.5em] text-ink-muted">/{QUIZ_MAX}</span>
                </p>
                <h2
                  id={`${formId}-heading`}
                  data-focus=""
                  tabIndex={-1}
                  className="mt-6 font-display text-[1.875rem] font-semibold leading-tight text-brand-primary outline-none"
                >
                  {tx(band.title)}
                </h2>
                <p className="mt-3 max-w-xl leading-relaxed text-ink-soft">{tx(band.body)}</p>
              </div>

              <div className="mt-10 rounded-[1.75rem] bg-teal-soft p-7 sm:p-9">
                <p className="text-sm font-semibold uppercase tracking-[0.14em] text-teal-strong">
                  {tx(QUIZ_UI.weakest)}: {tx(weak.name)}
                </p>
                <p className="mt-3 leading-relaxed text-ink-soft">{tx(weak.tip)}</p>
                <p className="mt-4 font-semibold leading-relaxed text-brand-primary">{tx(band.next)}</p>
                <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-start">
                  <div>
                    <WhatsAppButton
                      location="quiz_result"
                      variant="primary"
                      label={t("requestAuditCta")}
                      message={whatsappMessage}
                    />
                    <CtaReassurance className="mt-3" />
                  </div>
                  <AuditButton location="quiz_result" />
                </div>
              </div>

              <p className="mt-6 text-center text-sm text-ink-muted">{tx(QUIZ_UI.disclaimer)}</p>
              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={restart}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-ink-muted hover:text-brand-primary"
                >
                  <RotateCcw aria-hidden="true" className="size-4" />
                  {tx(QUIZ_UI.retake)}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </section>
    </main>
  );
}
